<template>
  <div>
    <h1 class="text-center">
      {{ $t('welcome') }}
    </h1>
    <p v-if="!isEnrollmentDisabled" class="lead text-center">
      <i18n path="portal.signInInstructions">
        <router-link :to="{name: 'enrollment'}">
          {{ $t('clickHere') }}
        </router-link>
      </i18n>
    </p>
    <div class="row justify-content-center">
      <b-alert :show="showLinkRequestSuggestion" type="info" class="col-md-6">
        <p><strong>{{ $t('portal.havingTroubleLoggingIn') }}</strong></p>
        <p>
          <i18n path="portal.accountInDifferentProgram">
            <a href="#" @click.prevent="openLinkRequestModal" class="alert-link">
              {{ $t('clickHere') | capitalize }}
            </a>
          </i18n>
        </p>
      </b-alert>
    </div>
    <div class="row justify-content-center">
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h3 class="mb-0">
              {{ $t('signIn') }}
            </h3>
          </div>
          <div class="card-body">
            <b-alert :show="showTimeoutMessage" type="danger" dismissible>
              {{ $t('session.timeoutAlert') }}
            </b-alert>
            <b-alert v-model="showError" type="danger" dismissible>
              <span v-html="$sanitize(globalError)" />
            </b-alert>
            <form @submit.prevent="login">
              <div class="form-group" :class="{'has-errors': errors.has('username')}">
                <label for="signin_username">{{ $t('portal.usernameOrEmail') }}</label>
                <input
                  id="signin_username"
                  v-model="username"
                  v-validate="'required'"
                  type="text"
                  name="username"
                  class="form-control"
                >
                <ul v-if="errors.has('username')" class="error-list">
                  <li>{{ errors.first('username') }}</li>
                </ul>
              </div>

              <div class="form-group" :class="{'has-errors': errors.has('password')}">
                <label for="signin_password">{{ $t('portal.password') }}</label>
                <input
                  id="signin_password"
                  v-model="password"
                  v-validate="'required'"
                  type="password"
                  name="password"
                  class="form-control"
                >
                <ul v-if="errors.has('password')" class="error-list">
                  <li>{{ errors.first('password') }}</li>
                </ul>
              </div>

              <input type="submit" class="btn btn-primary btn-block" :value="$t('signIn')">

              <p>
                <router-link :to="{name: 'reset-password'}">
                  {{ $t('portal.forgotPassword') }}
                </router-link>
              </p>
              <p>
                <a href="#" @click.prevent="openLinkRequestModal">{{ $t('portal.notSure') }}</a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
    <b-modal
      v-model="showLinkRequestModal"
      :title="$t('portal.findMyLogin')"
    >
      <p>{{ $t('portal.linkRequestInstructions') }}</p>

      <form @submit.prevent="getLoginLinks" v-if="!linkRequestComplete">
        <div class="form-group">
          <label>{{ $t('portal.usernameOrEmail') }}</label>
          <input v-model="account_identifier" name="account_identifier" type="text" class="form-control">
        </div>
        <input
          type="submit"
          class="btn btn-primary"
          :class="{'btn-loading': isSubmittingLinkRequest}"
          :disabled="isSubmittingLinkRequest"
          :value="$t('portal.sendMeMyLogin')"
        >
      </form>
      <div class="alert alert-success" v-else-if="linkRequestComplete == 'success'">
        {{ $t('portal.linkRequestSuccess') }}
      </div>
      <div class="alert alert-danger" v-else-if="linkRequestComplete == 'fail'">
        {{ $t('portal.linkRequestFail') }}
      </div>

      <div slot="modal-footer"></div>
    </b-modal>
  </div>
</template>

<script>
  import { BAlert, BModal } from "bootstrap-vue";

  export default {
    name: "Login",
    components: {
      BAlert,
      BModal,
    },
    data() {
      return {
        username: '',
        password: '',
        showError: false,
        globalError: '',
        showLinkRequestModal: false,
        account_identifier: '',
        isSubmittingLinkRequest: false,
        linkRequestComplete: '',
        loginFailures: 0,
      };
    },
    computed: {
      showTimeoutMessage() {
        return this.$route.params.timeout;
      },
      isEnrollmentDisabled() {
        return this.$store.getters.isStudyEnrollmentDisabled;
      },
      showLinkRequestSuggestion() {
        return this.loginFailures >= 2;
      }
    },
    methods: {
      openLinkRequestModal() {
        this.account_identifier = this.username;
        this.showLinkRequestModal = true;
      },
      async getLoginLinks() {
        // vee-validate is already handling the login form, just use an alert here
        if (!this.account_identifier.trim()) {
          window.alert(this.$t('portal.usernameOrEmailRequired'));
          return;
        }
        this.isSubmittingLinkRequest = true;
        try {
          await this.$store.dispatch('LOGIN_LINK_REQUEST', {account_identifier: this.account_identifier});
          this.linkRequestComplete = 'success';
        } catch (err) {
          this.linkRequestComplete = 'fail';
        }
        this.isSubmittingLinkRequest = false;
      },
      async login() {
        this.$store.commit('SET_LOADING', true);
        try {
          let res = await this.$validator.validate();
          if (!res) {
            return;
          }
          await this.$store.dispatch('LOGIN', {username: this.username, password: this.password});

          // https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
          this.$router.push({name: 'home'}).catch(e => {
          });
        } catch (err) {
          this.loginFailures++;
          if (err.response && err.response.body) {
            if (err.response.body.errors && err.response.body.errors.length) {
              // there shouldn't be more than one, if there is just line break between them.
              this.globalError = err.response.body.errors.join('<br>');
              this.showError = true;
            }
            if (err.response.body.field_errors) {
              err.response.body.field_errors.forEach(error => {
                this.errors.add({
                  field: error.field,
                  msg: error.message
                });
              });
            }
          }
          this.$store.commit('SET_LOADING', false);
        }
      }
    },
    filters: {
      capitalize(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
      }
    }
  };
</script>

